import { loadRuntimeConfig } from "../config/runtimeConfig.js";
import { joinUrl } from "../api/apiClient.js";

/**
 * Fetch seam for the P091R public invite/register API
 * (GET /api/public/invite-card, POST /api/public/worker-applications).
 *
 * Deliberately NOT the shared apiClient: apiRequest() in ../api/apiClient.js
 * always attaches an Authorization header when a session token happens to
 * exist in this browser tab (e.g. an Agent previewing their own invite
 * link while still signed in to the backoffice). These two routes are
 * unauthenticated by design (Prompt 091R) - sending a bearer token to them
 * would be harmless to the backend (they never check it) but is still the
 * wrong contract to model in the frontend, so this module reimplements the
 * same "wait for runtime config, then fetch" seam without ever touching
 * auth/session.js or reading a token.
 */

export class PublicApiError extends Error {
  constructor(message, { status = null, requestId = null, cause = null } = {}) {
    super(message);
    this.name = "PublicApiError";
    this.status = status;
    this.requestId = requestId;
    this.cause = cause;
  }
}

function generateRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `req_${crypto.randomUUID()}`;
  }
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

// Exported for the employer half of public onboarding (P156), which needs
// exactly this seam - no Authorization header, runtime config first - against
// its own separate routes.
export async function publicRequest(path, options = {}) {
  const { fetchImpl, headers, ...rest } = options;

  const config = await loadRuntimeConfig(fetchImpl ? { fetchImpl } : undefined);
  if (!config.apiBaseUrl) {
    throw new PublicApiError("Runtime config has no value for apiBaseUrl");
  }

  const url = joinUrl(config.apiBaseUrl, path);
  const requestId = generateRequestId();

  // No Authorization header, ever - see the module doc comment above.
  const requestHeaders = {
    "Content-Type": "application/json",
    "X-Request-Id": requestId,
    ...headers,
  };

  const doFetch = fetchImpl ?? (typeof fetch === "function" ? fetch.bind(globalThis) : undefined);
  if (typeof doFetch !== "function") {
    throw new PublicApiError("No fetch implementation available", { requestId });
  }

  let response;
  try {
    response = await doFetch(url, { ...rest, headers: requestHeaders });
  } catch (networkError) {
    throw new PublicApiError("Network request failed", { requestId, cause: networkError?.message });
  }

  const contentType = response.headers?.get?.("content-type") ?? "";
  const body = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    // The backend's own error messages are already public-safe (Prompt
    // 091R never leaks a config value or internal id in a 4xx/5xx body),
    // so surfacing body.message directly here is safe and gives the
    // worker a readable reason instead of a generic status code.
    const message = (body && typeof body === "object" && body.message) || `Request failed with status ${response.status}`;
    throw new PublicApiError(message, { status: response.status, requestId });
  }

  return body;
}

export function getPublicInviteCard(inviteCode, options) {
  return publicRequest(`/public/invite-card?invite=${encodeURIComponent(inviteCode)}`, { ...options, method: "GET" });
}

export function submitWorkerApplication(payload, options) {
  return publicRequest("/public/worker-applications", {
    ...options,
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Prompt 139B: posts to the real Prompt 139A multipart route
 * (POST /api/public/worker-applications/document) - the only route that
 * accepts primarySkillCode/passportNumber/a real passport document file
 * together with the rest of quick-register. Deliberately bypasses
 * publicRequest() above: that helper always sets
 * "Content-Type: application/json", which would break a multipart/
 * form-data body - the browser must set its own Content-Type (with the
 * random boundary string) from the FormData object itself, so no
 * Content-Type header is set here at all.
 */
export async function submitWorkerApplicationWithDocument(formData, options = {}) {
  const { fetchImpl } = options;

  const config = await loadRuntimeConfig(fetchImpl ? { fetchImpl } : undefined);
  if (!config.apiBaseUrl) {
    throw new PublicApiError("Runtime config has no value for apiBaseUrl");
  }

  const url = joinUrl(config.apiBaseUrl, "/public/worker-applications/document");
  const requestId = generateRequestId();

  const doFetch = fetchImpl ?? (typeof fetch === "function" ? fetch.bind(globalThis) : undefined);
  if (typeof doFetch !== "function") {
    throw new PublicApiError("No fetch implementation available", { requestId });
  }

  let response;
  try {
    // No Authorization header (see the module doc comment) and no
    // Content-Type header (see this function's own doc comment) - only the
    // request id, matching every other request this module sends.
    response = await doFetch(url, { method: "POST", headers: { "X-Request-Id": requestId }, body: formData });
  } catch (networkError) {
    throw new PublicApiError("Network request failed", { requestId, cause: networkError?.message });
  }

  const contentType = response.headers?.get?.("content-type") ?? "";
  const body = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    const message = (body && typeof body === "object" && body.message) || `Request failed with status ${response.status}`;
    throw new PublicApiError(message, { status: response.status, requestId });
  }

  return body;
}
